import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  StochasticInvoice,
  FetchStochasticInvoicesRequest,
  FetchStochasticInvoicesResponse,
} from "@/api/fetchStochasticInvoices/types";
import { AppDispatch, AppThunk } from "@/app/types";
import { fetchStochasticInvoices } from "@/api/fetchStochasticInvoices/fetchStochasticInvoicesApi";

interface StochasticInvoicesState {
  invoices: StochasticInvoice[];
  totalCount: number;
  loading: boolean;
  error: string | null;
}

const initialState: StochasticInvoicesState = {
  invoices: [],
  totalCount: 0,
  loading: false,
  error: null,
};

const stochasticInvoicesSlice = createSlice({
  name: "stochasticInvoices",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setInvoicesData: (
      state,
      action: PayloadAction<{
        invoices: StochasticInvoice[];
        totalCount: number;
      }>,
    ) => {
      state.invoices = action.payload.invoices;
      state.totalCount = action.payload.totalCount;
    },
    resetStochasticInvoices: () => initialState,
  },
});

export const {
  setLoading,
  setError,
  setInvoicesData,
  resetStochasticInvoices,
} = stochasticInvoicesSlice.actions;

export const fetchStochasticInvoicesAction =
  (requestData: FetchStochasticInvoicesRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      const response: FetchStochasticInvoicesResponse =
        await fetchStochasticInvoices(requestData);

      dispatch(
        setInvoicesData({
          invoices: response.data,
          totalCount: response.meta?.totalCount || 0,
        }),
      );
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to fetch invoices"));
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

export default stochasticInvoicesSlice.reducer;
